import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type QrType = "admin" | "academic" | "examination" | "accounting";

interface UseRotatingQrTokenResult {
  token: string | null;
  expiresAt: number | null;
  ttlMs: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const WINDOW_SEC = 30; // 30-second TOTP window — matches verify-student-qr
const WINDOW_MS = WINDOW_SEC * 1000;
const STORAGE_PREFIX = "qr_secret_v2:"; // localStorage key prefix
const SECRET_REFRESH_MS = 24 * 60 * 60 * 1000; // re-fetch from server once a day

interface CachedSecret {
  studentId: string;
  secret: string;
  fetchedAt: number;
}

function loadCached(qrType: QrType): CachedSecret | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + qrType);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedSecret;
    if (!parsed.secret || !parsed.studentId) return null;
    if (Date.now() - parsed.fetchedAt > SECRET_REFRESH_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveCached(qrType: QrType, value: CachedSecret) {
  try {
    localStorage.setItem(STORAGE_PREFIX + qrType, JSON.stringify(value));
  } catch {
    /* storage might be full or disabled — ignore */
  }
}

function hexFromBytes(bytes: Uint8Array): string {
  let hex = "";
  for (const b of bytes) hex += b.toString(16).padStart(2, "0");
  return hex;
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.substr(i * 2, 2), 16);
  return out;
}

async function hmacHex(secretHex: string, message: string): Promise<string> {
  const keyBytes = hexToBytes(secretHex);
  const key = await crypto.subtle.importKey(
    "raw",
    keyBytes.buffer.slice(keyBytes.byteOffset, keyBytes.byteOffset + keyBytes.byteLength) as ArrayBuffer,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const msgBytes = new TextEncoder().encode(message);
  const sig = await crypto.subtle.sign("HMAC", key, msgBytes.buffer.slice(msgBytes.byteOffset, msgBytes.byteOffset + msgBytes.byteLength) as ArrayBuffer);
  return hexFromBytes(new Uint8Array(sig));
}

/**
 * Generates short-lived QR tokens locally on the device (TOTP-style).
 *
 * The student's per-purpose HMAC secret is fetched once from the server and
 * cached in localStorage for 24 hours. Tokens then rotate every 30 seconds
 * computed entirely on-device — no network calls per rotation, works offline,
 * and the screen does not blink because the underlying QR string only changes
 * on a fresh window.
 *
 * The server (`verify-student-qr`) accepts the current 30s window ± 1 to tolerate
 * mild phone-clock drift; screenshots become invalid within ~60 seconds.
 */
export function useRotatingQrToken(qrType: QrType, enabled = true): UseRotatingQrTokenResult {
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cachedRef = useRef<CachedSecret | null>(null);
  const aborted = useRef(false);
  const lastWindowRef = useRef<number>(-1);

  const generate = useCallback(async () => {
    const cached = cachedRef.current;
    if (!cached) return;
    const now = Date.now();
    const windowIdx = Math.floor(now / WINDOW_MS);
    if (windowIdx === lastWindowRef.current) return; // same window — token unchanged
    lastWindowRef.current = windowIdx;

    const payload = `${qrType}.${cached.studentId}.${windowIdx}`;
    const sig = await hmacHex(cached.secret, payload);
    const next = `v2.${payload}.${sig}`;
    if (aborted.current) return;
    setToken(next);
    setExpiresAt((windowIdx + 1) * WINDOW_MS);
  }, [qrType]);

  const fetchSecret = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Try cache first — works offline
      const cached = loadCached(qrType);
      if (cached) {
        cachedRef.current = cached;
        await generate();
        setLoading(false);
        // refresh in background if online (don't block UI)
        return;
      }

      // 2. Fetch from server
      const { data, error: rpcErr } = await supabase.rpc("get_student_qr_secret_for_self", {
        _qr_type: qrType,
      });
      if (aborted.current) return;
      if (rpcErr) {
        setError(rpcErr.message);
        return;
      }
      const row = Array.isArray(data) ? data[0] : data;
      if (!row?.secret || !row?.student_id) {
        setError("No QR secret returned");
        return;
      }
      const fresh: CachedSecret = {
        studentId: row.student_id as string,
        secret: row.secret as string,
        fetchedAt: Date.now(),
      };
      cachedRef.current = fresh;
      saveCached(qrType, fresh);
      lastWindowRef.current = -1; // force regenerate
      await generate();
    } catch (e) {
      if (!aborted.current) setError((e as Error).message);
    } finally {
      if (!aborted.current) setLoading(false);
    }
  }, [qrType, generate]);

  useEffect(() => {
    aborted.current = false;
    if (!enabled) return;
    void fetchSecret();
    // Tick every second to detect window boundary; generate() is a no-op within same window.
    const id = window.setInterval(() => {
      void generate();
    }, 1000);
    return () => {
      aborted.current = true;
      window.clearInterval(id);
    };
  }, [enabled, fetchSecret, generate]);

  return {
    token,
    expiresAt,
    ttlMs: WINDOW_MS,
    loading,
    error,
    refresh: () => {
      // Force re-fetch secret from server (e.g. user tapped "Refresh")
      try {
        localStorage.removeItem(STORAGE_PREFIX + qrType);
      } catch {
        /* ignore */
      }
      cachedRef.current = null;
      lastWindowRef.current = -1;
      void fetchSecret();
    },
  };
}
